import { useQuery, useMutation } from '@tanstack/react-query'
import { BrandsFilterParams, CreateBrandParams, UpdateBrandParams } from '@/types/brand'
import { useBrandsStore } from '@/store'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useBrands = (params: BrandsFilterParams, options?: { enabled?: boolean }) => {
  const fetchBrands = useBrandsStore(state => state.fetchBrands)

  return useQuery({
    queryKey: ['brands', params],
    queryFn: () => fetchBrands(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: options?.enabled ?? true
  })
}

export const useBrand = (id: string) => {
  const getBrand = useBrandsStore(state => state.getBrand)

  return useQuery({
    queryKey: ['brand', id],
    queryFn: () => getBrand(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })
}

export const useCreateBrand = () => {
  const createBrand = useBrandsStore(state => state.createBrand)

  return useMutation({
    mutationFn: (params: CreateBrandParams) => createBrand(params),
    onSuccess: () => {}
  })
}

export const useUpdateBrand = () => {
  const updateBrand = useBrandsStore(state => state.updateBrand)

  return useMutation({
    mutationFn: ({ brandId, params }: { brandId: string; params: UpdateBrandParams }) => updateBrand(brandId, params),
    onSuccess: () => {}
  })
}

export const useDeleteBrand = () => {
  const deleteBrand = useBrandsStore(state => state.deleteBrand)

  return useMutation({
    mutationFn: (id: string) => deleteBrand(id),
    onSuccess: () => {}
  })
}

export const useBulkDeleteBrands = () => {
  const bulkDeleteBrands = useBrandsStore(state => state.bulkDeleteBrands)
  const deselectAllBrands = useBrandsStore(state => state.deselectAllBrands)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteBrands(ids),
    onSuccess: response => {
      deselectAllBrands()
      handleBulkDeleteSuccess(response)
    }
  })
}
